import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import { ConnectionStatus, ConnectionType, Network } from '@capacitor/network';
import { LoggingService } from './logging.service';

@Injectable({ providedIn: 'root' })
export class NetworkService implements OnDestroy {
  private readonly loggingService = inject(LoggingService);
  private readonly _isOnline = signal(true);
  private readonly _connectionType = signal<ConnectionType>('unknown');
  private listener: PluginListenerHandle | null = null;

  readonly isOnline = this._isOnline.asReadonly();

  readonly connectionType = this._connectionType.asReadonly();

  constructor() {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      const status = await Network.getStatus();
      this.updateStatus(status);

      this.listener = await Network.addListener('networkStatusChange', (status) => {
        this.updateStatus(status);
      });
    } catch (error) {
      this.loggingService.logException(error, 'NetworkService: Failed to initialize network monitoring');
      this._isOnline.set(navigator.onLine);
      window.addEventListener('online', () => this._isOnline.set(true));
      window.addEventListener('offline', () => this._isOnline.set(false));
    }
  }

  private updateStatus(status: ConnectionStatus): void {
    this._isOnline.set(status.connected);
    this._connectionType.set(status.connectionType);
  }

  async checkConnection(): Promise<boolean> {
    try {
      const status = await Network.getStatus();
      this.updateStatus(status);
      return status.connected;
    } catch {
      return navigator.onLine;
    }
  }

  isCellular(): boolean {
    return this._connectionType() === 'cellular';
  }

  isWifi(): boolean {
    return this._connectionType() === 'wifi';
  }

  ngOnDestroy(): void {
    this.listener?.remove();
  }
}
